import {
  ASPECT_RATIOS,
  calculateInitialCropRect,
  findBestForMaxArea,
  type AspectRatioKey,
} from '@/shared/constants/aspect-ratios';
import {
  findPhotoBounds,
  scaleRectToNatural,
  TRIM_SPEC,
  type PixelSource,
  type TrimRect,
} from '@/shared/lib/image-trim';
import {
  computeTargetDimensions,
  resolveImageSizePolicy,
  type ImageSizePolicy,
} from '@/shared/lib/services/image-upload.service';
import { normalizeHeicFile } from '@/shared/lib/heic';
import type { UploadType } from '@/shared/constants';
import type { CropArea, ImageNaturalSize } from './types';

/**
 * The frame the cropper opens with, in one place: the cropper's initial state
 * and the first-run wizard (which applies it with no cropper UI) both read it,
 * so "confirm the untouched frame" and "auto-crop" cut the same pixels.
 *
 * Steps: content bounds (letterbox / screenshot chrome) → the supported ratio
 * that keeps the most of that content (max coverage) → the largest centred rect of that ratio inside it.
 */

/** The ratio the cropper selects for this content. */
export function defaultCropRatioKey(frame: CropArea): AspectRatioKey {
  return findBestForMaxArea(frame.width, frame.height);
}

/** Largest rect of `ratio` centred inside `frame`, in the frame's coordinates. */
export function fitCropRect(frame: CropArea, ratio: number): CropArea {
  const rect = calculateInitialCropRect(frame.width, frame.height, ratio);
  return {
    x: frame.x + rect.x,
    y: frame.y + rect.y,
    width: rect.width,
    height: rect.height,
  };
}

/** The whole default: ratio + rect for an image with (optional) content bounds. */
export function computeDefaultCrop(
  naturalSize: ImageNaturalSize,
  contentBounds: TrimRect | null
): { ratioKey: AspectRatioKey; rect: CropArea } {
  const frame = contentBounds ?? {
    x: 0,
    y: 0,
    width: naturalSize.width,
    height: naturalSize.height,
  };
  const ratioKey = defaultCropRatioKey(frame);
  return { ratioKey, rect: fitCropRect(frame, ASPECT_RATIOS[ratioKey].ratio) };
}

/** Scale a natural-size image down to the letterbox scan's working copy. */
export function contentScanScale(naturalSize: ImageNaturalSize): number {
  return Math.max(
    1,
    Math.max(naturalSize.width, naturalSize.height) / TRIM_SPEC.workingDimension
  );
}

/** Content bounds in natural pixels from the working copy's pixels, or null. */
export function contentBoundsFromPixels(
  pixels: PixelSource,
  scale: number,
  naturalSize: ImageNaturalSize
): TrimRect | null {
  const found = findPhotoBounds(pixels);
  return found ? scaleRectToNatural(found, scale, naturalSize) : null;
}

/**
 * Run the letterbox scan on a decoded image. Null when there is no border to
 * drop (or the canvas is unavailable): the caller then frames the whole image.
 */
export function scanContentBounds(
  image: CanvasImageSource,
  naturalSize: ImageNaturalSize
): TrimRect | null {
  const scale = contentScanScale(naturalSize);
  const width = Math.max(1, Math.round(naturalSize.width / scale));
  const height = Math.max(1, Math.round(naturalSize.height / scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(image, 0, 0, width, height);
  return contentBoundsFromPixels(
    context.getImageData(0, 0, width, height),
    scale,
    naturalSize
  );
}

/**
 * Cut `area` out of the image, downscale by the shared size policy and encode
 * JPEG down the quality ladder (0.8 → 0.3) until it fits the byte budget.
 * The canvas is drawn synchronously, so the caller may release the source
 * right after this returns.
 */
export function cropImageToJpeg(
  image: CanvasImageSource,
  area: CropArea,
  sizePolicy: Pick<ImageSizePolicy, 'mode' | 'target'>,
  maxFileSize: number
): { canvas: HTMLCanvasElement; blobPromise: Promise<Blob | null> } | null {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const { width, height } = computeTargetDimensions(
    area.width,
    area.height,
    sizePolicy
  );
  canvas.width = width;
  canvas.height = height;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, width, height);

  const blobPromise = (async (): Promise<Blob | null> => {
    const encode = (q: number): Promise<Blob | null> =>
      new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
    let blob: Blob | null = null;
    for (let quality = 0.8; quality >= 0.3; quality -= 0.1) {
      blob = await encode(quality);
      if (blob && blob.size <= maxFileSize) break;
    }
    return blob;
  })();

  return { canvas, blobPromise };
}

/**
 * Apply the cropper's default frame to a picked file with no cropper UI:
 * HEIC decoded to JPEG first (like the upload picker), EXIF orientation applied
 * by the browser's image decoding (like the cropper's <img>), then the same
 * rect, size policy and byte budget as confirming the untouched frame.
 */
export async function autoCropImageFile(
  file: File,
  uploadType: UploadType
): Promise<File> {
  const source = await normalizeHeicFile(file);
  const url = URL.createObjectURL(source);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const naturalSize = {
      width: image.naturalWidth,
      height: image.naturalHeight,
    };

    const { rect } = computeDefaultCrop(
      naturalSize,
      scanContentBounds(image, naturalSize)
    );
    const policy = resolveImageSizePolicy(uploadType);
    const cropped = cropImageToJpeg(image, rect, policy, policy.maxFileBytes);
    const blob = cropped ? await cropped.blobPromise : null;
    if (!blob) {
      throw new Error('Auto-crop produced no image');
    }
    return new File([blob], source.name, { type: blob.type });
  } finally {
    URL.revokeObjectURL(url);
  }
}
